import { Component, computed, effect, inject, input, model, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { debounce, timer } from 'rxjs';

import { BaasBreadcrumb } from '../../baas-breadcrumb/baas-breadcrumb';
import { BaasBreadcrumbOptions } from '../../baas-breadcrumb/baas-breadcrumb-options.type';
import { BaasConfirmService } from '../../baas-confirm/baas-confirm.service';
import { BaasHierarchicalAction } from '../baas-hierarchical-action.type';
import { BaasHierarchicalActionConfig, createDefaultBaasHierarchicalActionConfig } from '../baas-hierarchical-action-config.type';
import { BaasHierarchicalListItem } from '../baas-hierarchical-list-item/baas-hierarchical-list-item';
import { BaasHierarchicalListItemActionInfo } from '../baas-hierarchical-list-item-action-info.type';
import { BaasHierarchicalListItemMoveConfig } from '../baas-hierarchical-list-item-move-config.type';

@Component({
    selector: 'baas-hierarchical-list',
    templateUrl: './baas-hierarchical-list.html',
    imports: [ReactiveFormsModule, MatFormFieldModule, MatButtonModule, MatIconModule, MatInputModule, BaasBreadcrumb, BaasHierarchicalListItem]
})
export class BaasHierarchicalList {
    readonly #confirmService = inject(BaasConfirmService);
    /* Optional. By default all actions are enabled. */
    readonly actionConfig = input<BaasHierarchicalActionConfig>(createDefaultBaasHierarchicalActionConfig());
    /* Complete hierarchical structure */
    readonly items = input<any[]>();
    /* The field of the provided items to use for displaying the title in the cards */
    readonly itemLabelField = input('title');
    /* The field of the provided items to use as unique identifier */
    readonly itemIdField = input('id');
    /* The field of the provided items to use as an alternative tooltip */
    readonly itemTooltipField = input('tooltip');
    /* If the component is used in a dialog, delete action needs to be handled by application (dialog in dialog not possible) */
    readonly dialog = input(false);
    /* An optional width declaration for the list */
    readonly listWidth = input('auto');
    /* Action type of the click on the text in the list item, default is edit */
    readonly itemClickActionType = input<BaasHierarchicalAction>('edit');
    /* Wether or not to hide the action button defined as itemClickActionType (global or on item level) */
    readonly itemClickHideAction = input(false);
    /* Whether to show the filter above the list. This will filter the root elements and the children. */
    readonly filterable = input(false);
    /* Pass breadcrumbs so a nested list can be directly viewed */
    readonly breadcrumbs = model<BaasBreadcrumbOptions[]>([]);
    /* Outputs the specific component to view */
    readonly view = output<BaasHierarchicalListItemActionInfo>();
    /* Outputs the parent component, where to create the new item in as a child */
    readonly create = output<BaasHierarchicalListItemActionInfo>();
    /* Outputs the specific component to edit */
    readonly edit = output<BaasHierarchicalListItemActionInfo>();
    /* Outputs the specific component to delete */
    readonly delete = output<BaasHierarchicalListItemActionInfo>();
    /* Outputs an object with all old and new location info (parent+index), if parent changed,... */
    readonly move = output<BaasHierarchicalListItemMoveConfig>();

    protected readonly moveConfig = signal<BaasHierarchicalListItemMoveConfig>({} as BaasHierarchicalListItemMoveConfig);
    protected readonly moveEnabled = signal(false);
    protected readonly filterTextControl = new FormControl<string>(undefined);
    protected readonly filteredText = toSignal(
        this.filterTextControl.valueChanges.pipe(
            takeUntilDestroyed(),
            debounce(() => timer(500))
        )
    );
    protected readonly parent = computed(() => {
        return this.breadcrumbs().length ? this.breadcrumbs()[this.breadcrumbs().length - 1].item : null;
    });
    protected readonly filteredList = computed(() => {
        if (!this.filterable() || !this.filteredText()) return this.#activeList();
        return this.#activeList().filter((item) => this.#checkFilterMatchRecursive(item));
    });
    readonly #activeList = signal<any[]>([]);

    constructor() {
        effect(() => {
            // effect only when items change
            this.items();
            untracked(() => {
                // nested item
                if (this.breadcrumbs().length) {
                    let parent = null;
                    this.breadcrumbs().forEach((crumb: BaasBreadcrumbOptions, index: number) => {
                        parent = index === 0 ? this.items()[crumb.index] : parent.children[crumb.index];
                    });
                    this.#activeList.set(parent.children ? parent.children : []);
                } else {
                    return this.#activeList.set(this.items());
                }
            });
        });
    }

    protected doAction(actionType: BaasHierarchicalAction, item: any, index: number): void {
        switch (actionType) {
            case 'create': {
                let path = null;
                // 1. Add btn clicked on specific item: create new item as child of provided item
                if (item) {
                    path = [...this.breadcrumbs(), { item, index }];
                    // 2. Add button on top clicked
                    // 2.1. Breadcrumb active (nested parent item)
                } else if (this.breadcrumbs().length) {
                    path = [...this.breadcrumbs()];
                }
                // 2.2 Add to root: path = null

                this.create.emit({ item: null, path });
                break;
            }
            case 'edit': {
                this.edit.emit({ item, path: [...this.breadcrumbs()] });
                break;
            }
            case 'view': {
                this.view.emit({ item, path: [...this.breadcrumbs()] });
                break;
            }
            case 'delete': {
                // responsibility of the application to show the delete prompt when dialog is true (dialog in dialog is not possible)
                if (!this.dialog()) {
                    this.#confirmService
                        .confirm({
                            message: `Bent je zeker dat je item met titel "${item[this.itemLabelField()]}" wilt verwijderen?`,
                            title: 'Verwijderen'
                        })
                        .subscribe(() => {
                            this.delete.emit({ item, path: [...this.breadcrumbs()] });
                        });
                } else {
                    this.delete.emit({ item, path: [...this.breadcrumbs()] });
                }
                break;
            }
            case 'move': {
                this.moveConfig.set({
                    ...({} as BaasHierarchicalListItemMoveConfig),
                    oldParent: this.parent(),
                    oldIndexWithinParent: index,
                    item
                });
                this.moveEnabled.set(true);
                break;
            }
            case 'navigate': {
                this.#activeList.set(item.children);
                this.breadcrumbs.update((breadcrumbs) => [...breadcrumbs, { item, index }]);
                break;
            }
        }
    }

    protected moveItem(index: number): void {
        const newParent = this.parent();
        this.moveConfig.update((moveConfig) => {
            return {
                ...moveConfig,
                newParent,
                newIndexWithinParent: index,
                parentChanged: this.hasParentChanged(moveConfig.oldParent, newParent)
            };
        });

        this.move.emit(this.moveConfig());
        // reset move
        this.moveCancel();
    }

    protected moveCancel(): void {
        this.moveEnabled.set(false);
        this.moveConfig.set({} as BaasHierarchicalListItemMoveConfig);
    }

    protected navigateCrumb(value): void {
        const { crumb, crumbIndex } = value;
        if (crumb && crumb.item) {
            this.#activeList.set(crumb.item.children);
        } else {
            // home breadcrumb clicked
            this.#activeList.set(this.items());
        }

        this.breadcrumbs.update((breadcrumbs) => {
            // NOTE splice is a mutable function
            breadcrumbs.splice(crumbIndex + 1);
            // return new instance to trigger change detection
            return [...breadcrumbs];
        });
    }

    // check if maybe equal (in case both null), then check if one of the is null and then compare objects against each other
    protected hasParentChanged(oldParent: any, newParent: any): boolean {
        return (
            oldParent != newParent ||
            (oldParent == newParent && oldParent != null && newParent != null && oldParent[this.itemIdField()] != newParent[this.itemIdField()])
        );
    }

    protected clearFilter(): void {
        this.filterTextControl.setValue(null);
    }

    #checkFilterMatchRecursive(item: any): boolean {
        const label = item[this.itemLabelField()] || '';
        if (label.toUpperCase().indexOf(this.filterTextControl.value.toUpperCase()) > -1) return true;

        if (item.children) {
            for (const child of item.children) {
                if (this.#checkFilterMatchRecursive(child)) {
                    return true;
                }
            }
        }

        return false;
    }
}
